import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users, Building2, MessageSquare, BarChart3, Shield,
  CheckCircle, Trash2, RefreshCw, ChevronDown, ChevronRight,
  UserX, User2, Heart, Trophy, Loader, Receipt, Search, ExternalLink
} from 'lucide-react';
import UserDetailModal from '../admin/UserDetailModal';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = 'rentalx-admin-2026';

const AdminPanel = () => {
  const [params] = useSearchParams();
  const key = params.get('key');

  const [tab, setTab] = useState('pending');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [enhancedUsers, setEnhancedUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [vibeProfiles, setVibeProfiles] = useState([]);
  const [expandedConv, setExpandedConv] = useState(null);
  const [convMessages, setConvMessages] = useState({});
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingMsgText, setEditingMsgText] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [rejectModal, setRejectModal] = useState(null); // { id, name }
  const [rejectNote, setRejectNote] = useState('');
  const [expandedPending, setExpandedPending] = useState(null);

  if (key !== ADMIN_KEY) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-10 shadow-lg border border-red-100">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500 mb-6">Invalid or missing admin key.</p>
          <Link to="/" className="text-[#094f6f] underline text-sm">← Back to RentalX</Link>
        </div>
      </div>
    );
  }

  const q = (path) => `${API}${path}?key=${ADMIN_KEY}`;
  const headers = { 'Content-Type': 'application/json' };
  const toast = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 2500); };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, u, l, c, v, p, rc, eu] = await Promise.all([
        fetch(q('/api/admin/stats')).then(r => r.json()),
        fetch(q('/api/admin/users')).then(r => r.json()),
        fetch(q('/api/admin/listings')).then(r => r.json()),
        fetch(q('/api/admin/messages')).then(r => r.json()),
        fetch(q('/api/admin/vibe-profiles')).then(r => r.json()),
        fetch(q('/api/admin/pending')).then(r => r.json()),
        fetch(`${API}/api/receipts/admin/pending?key=${ADMIN_KEY}`).then(r => r.json()),
        fetch(`${API}/api/admin/users/enhanced?key=${ADMIN_KEY}&limit=100`).then(r => r.json()),
      ]);
      setStats(s);
      setUsers(Array.isArray(u) ? u : []);
      setListings(Array.isArray(l) ? l : []);
      setConversations(Array.isArray(c) ? c : []);
      setVibeProfiles(Array.isArray(v) ? v : []);
      setPendingListings(Array.isArray(p) ? p : []);
      setPendingReceipts(Array.isArray(rc) ? rc : []);
      setEnhancedUsers(Array.isArray(eu) ? eu : []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleUser = async (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    await fetch(`${API}/api/admin/users/${userId}/${action}?key=${ADMIN_KEY}`, { method: 'PATCH', headers });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !isActive } : u));
    toast(`User ${action}d`);
  };

  const [forcedOut, setForcedOut] = useState(new Set());

  const forceLogout = async (userId, name) => {
    if (!window.confirm(`Force logout ${name}? Their current session will be invalidated immediately.`)) return;
    await fetch(`${API}/api/admin/users/${userId}/force-logout?key=${ADMIN_KEY}`, { method: 'POST', headers });
    setForcedOut(prev => new Set([...prev, userId]));
    toast(`${name} has been logged out`);
  };

  const deleteConversation = async (convId) => {
    if (!window.confirm('Delete this entire conversation and all its messages?')) return;
    await fetch(`${API}/api/admin/conversations/${encodeURIComponent(convId)}?key=${ADMIN_KEY}`, { method: 'DELETE', headers });
    setConversations(prev => prev.filter(c => c.conversation_id !== convId));
    if (expandedConv === convId) setExpandedConv(null);
    toast('Conversation deleted');
  };

  const deleteMessage = async (msgId, convId) => {
    if (!window.confirm('Delete this message?')) return;
    await fetch(`${API}/api/admin/messages/${msgId}?key=${ADMIN_KEY}`, { method: 'DELETE', headers });
    setConvMessages(prev => ({
      ...prev,
      [convId]: prev[convId]?.filter(m => m.message_id !== msgId) || []
    }));
    toast('Message deleted');
  };


  const saveEditMessage = async (msgId, convId) => {
    if (!editingMsgText.trim()) return;
    const r = await fetch(
      `${API}/api/admin/messages/${msgId}?key=${ADMIN_KEY}&new_content=${encodeURIComponent(editingMsgText.trim())}`,
      { method: 'PATCH', headers }
    );
    if (r.ok) {
      setConvMessages(prev => ({
        ...prev,
        [convId]: prev[convId]?.map(m =>
          m.message_id === msgId ? { ...m, content: editingMsgText.trim(), admin_edited: true } : m
        ) || []
      }));
      toast('Message edited');
    }
    setEditingMsgId(null);
    setEditingMsgText('');
  };


  const loadConvMessages = async (convId) => {
    if (expandedConv === convId) { setExpandedConv(null); return; }
    setExpandedConv(convId);
    if (!convMessages[convId]) {
      const msgs = await fetch(`${API}/api/admin/messages/${encodeURIComponent(convId)}?key=${ADMIN_KEY}`).then(r => r.json());
      setConvMessages(prev => ({ ...prev, [convId]: Array.isArray(msgs) ? msgs : [] }));
    }
  };

  const deleteVibeProfile = async (userId, name) => {
    if (!window.confirm(`Delete VibeMate profile for ${name}?`)) return;
    await fetch(`${API}/api/admin/vibe-profiles/${userId}?key=${ADMIN_KEY}`, { method: 'DELETE', headers });
    setVibeProfiles(prev => prev.filter(p => p.user_id !== userId));
    toast(`Profile deleted`);
  };

  const updateListingStatus = async (id, status) => {
    await fetch(`${API}/api/admin/listings/${id}/status?key=${ADMIN_KEY}&status=${status}`, { method: 'PATCH', headers });
    setListings(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    toast(`Listing ${status}`);
  };

  const deleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    await fetch(`${API}/api/admin/listings/${id}?key=${ADMIN_KEY}`, { method: 'DELETE', headers });
    setListings(prev => prev.filter(l => l.id !== id));
    toast('Listing deleted');
  };

  const StatCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="bg-white rounded-2xl border border-sky-100 p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-3xl font-black text-gray-900">{value}</p>
      <p className="text-sm font-semibold text-gray-700 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  const approveListing = async (id, name) => {
    await fetch(`${API}/api/admin/listings/${id}/approve?key=${ADMIN_KEY}`, { method: 'PATCH', headers });
    setPendingListings(prev => prev.filter(p => p.id !== id));
    toast(`✅ "${name}" approved and published`);
  };

  const openRejectModal = (listing) => { setRejectModal(listing); setRejectNote(''); };
  const submitReject = async () => {
    if (!rejectModal) return;
    await fetch(`${API}/api/admin/listings/${rejectModal.id}/reject?key=${ADMIN_KEY}&rejection_note=${encodeURIComponent(rejectNote)}`, { method: 'PATCH', headers });
    setPendingListings(prev => prev.filter(p => p.id !== rejectModal.id));
    toast(`"${rejectModal.name}" rejected — user notified`);
    setRejectModal(null); setRejectNote('');
  };

  const pendingCount = pendingListings.length;
  const sidebarItems = [
    { id: 'pending',  label: pendingCount > 0 ? `⚠️ Pending (${pendingCount})` : 'Pending Approval', icon: Shield },
    { id: 'stats',    label: 'Overview',                           icon: BarChart3 },
    { id: 'users',    label: `Users (${users.length})`,            icon: Users },
    { id: 'listings', label: `All Listings (${listings.length})`,  icon: Building2 },
    { id: 'messages', label: `Messages (${conversations.length})`, icon: MessageSquare },
    { id: 'vibe',          label: `VibeMate (${vibeProfiles.length})`,  icon: Heart },
    { id: 'receipts',      label: pendingReceipts.length > 0 ? `🧾 Receipts (${pendingReceipts.length})` : 'Rent Receipts', icon: Receipt },
    { id: 'verifications', label: 'ID Verifications', icon: Shield },
    { id: 'rewards',       label: 'Rewards',          icon: Trophy },
  ];

  const thClass = "px-4 py-3 text-left text-gray-600 font-semibold text-xs uppercase tracking-wide";
  const tdClass = "px-4 py-3";

  return (
    <>
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-60 bg-white border-r border-sky-100 flex flex-col">
        <div className="p-5 border-b border-sky-100">
          <div className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X" style={{ height: '1.3rem', width: 'auto', marginLeft: '0.04em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </div>
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Admin Panel</p>
        </div>

        <nav className="mt-3 flex-1">
          {sidebarItems.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)}
              className={`w-full flex items-center px-5 py-3 text-sm text-left transition-colors ${
                tab === item.id ? 'bg-sky-50 text-[#094f6f] border-r-2 border-[#094f6f] font-semibold' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              <item.icon className="w-4 h-4 mr-3" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-sky-100 space-y-2">
          <button onClick={fetchAll} className="w-full flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-[#094f6f] py-2 transition-colors">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <Link to="/" className="block text-center text-xs text-gray-400 hover:text-[#094f6f] transition-colors">← Back to site</Link>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 p-8 overflow-auto">
        {actionMsg && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg z-50 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />{actionMsg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#094f6f] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Pending Approval ── */}
            {tab === 'pending' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Pending Approval</h1>
                    <p className="text-sm text-gray-500 mt-1">Review and approve or reject property submissions before they go live.</p>
                  </div>
                  {pendingCount > 0 && (
                    <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full">{pendingCount} awaiting review</span>
                  )}
                </div>
                {pendingListings.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-sky-100">
                    <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-700">All caught up!</h3>
                    <p className="text-gray-400 text-sm mt-1">No listings waiting for approval.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingListings.map(p => (
                      <div key={p.id} className="bg-white rounded-2xl border border-amber-100 shadow-sm overflow-hidden">
                        {/* Header row */}
                        <div className="flex items-start justify-between p-5 gap-4">
                          <div className="flex gap-4 flex-1 min-w-0">
                            {p.images?.[0] ? (
                              <img src={p.images[0]} alt={p.name} className="w-20 h-16 rounded-xl object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-20 h-16 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-7 h-7 text-sky-200" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 mb-1">
                                <h3 className="font-bold text-gray-900 truncate">{p.name}</h3>
                                <span className="flex-shrink-0 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">PENDING</span>
                              </div>
                              <p className="text-sm text-gray-500">{p.city} · A${p.price_per_week}/wk · {p.room_type}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                Submitted by <strong>{p.submitter_name}</strong> ({p.submitter_email}) · {p.submitted_at?.split('T')[0]}
                              </p>
                            </div>
                          </div>
                          {/* Actions */}
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => setExpandedPending(expandedPending === p.id ? null : p.id)}
                              className="text-xs border border-gray-200 text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                              {expandedPending === p.id ? 'Hide' : 'View Details'}
                            </button>
                            <button onClick={() => approveListing(p.id, p.name)}
                              className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors font-semibold">
                              ✓ Approve
                            </button>
                            <button onClick={() => openRejectModal(p)}
                              className="text-xs border border-red-200 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors font-semibold">
                              ✕ Reject
                            </button>
                          </div>
                        </div>
                        {/* Expanded details */}
                        {expandedPending === p.id && (
                          <div className="border-t border-gray-100 p-5 bg-gray-50/50 space-y-3">
                            {p.description && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{p.description}</p>
                              </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              {[['Address', p.address],['Bedrooms', p.bedrooms],['Bathrooms', p.bathrooms],['University', p.university_nearby],['Available From', p.available_from],['Available Until', p.available_until || 'Ongoing']].map(([k,v]) => v ? (
                                <div key={k} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <p className="text-gray-400 mb-0.5">{k}</p>
                                  <p className="font-medium text-gray-800 text-xs">{v}</p>
                                </div>
                              ) : null)}
                            </div>
                            {p.amenities?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Amenities</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {p.amenities.map(a => <span key={a} className="text-xs bg-sky-50 text-[#094f6f] px-2 py-0.5 rounded-full">{a}</span>)}
                                </div>
                              </div>
                            )}
                            {p.images?.length > 1 && (
                              <div className="flex gap-2 overflow-x-auto pb-1">
                                {p.images.map((img, i) => (
                                  <img key={i} src={img} alt="" className="w-24 h-20 rounded-lg object-cover flex-shrink-0" />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Reject Modal */}
                {rejectModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Reject: "{rejectModal.name}"</h3>
                      <p className="text-sm text-gray-500 mb-4">An email will be sent to the user explaining the rejection. Optionally include a reason below.</p>
                      <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                        placeholder="e.g. Photos are required. Please add at least 3 photos of the property."
                        rows={4}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" />
                      <div className="flex gap-2">
                        <button onClick={() => setRejectModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                        <button onClick={submitReject} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors">Send Rejection</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {tab === 'stats' && stats && (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Platform Overview</h1>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={Users} label="Total Users" value={stats.users?.total || 0} sub={`${stats.users?.students || 0} students · ${stats.users?.landlords || 0} landlords`} color="bg-[#094f6f]" />
                  <StatCard icon={Building2} label="Listings" value={stats.listings?.total || 0} sub={`${stats.listings?.active || 0} active`} color="bg-[#2cbdc4]" />
                  <StatCard icon={MessageSquare} label="Messages" value={stats.messages?.total || 0} sub={`${stats.messages?.conversations || 0} conversations`} color="bg-purple-500" />
                  <StatCard icon={Heart} label="VibeMate" value={stats.vibe_profiles || 0} sub={`${stats.requirements || 0} requirements`} color="bg-orange-500" />
                </div>
              </div>
            )}

            {/* ── Users ── */}
            {tab === 'users' && (
              <div>
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                      placeholder="Search by name or email…"
                      className="pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/20 w-64" />
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 border-b border-gray-100">
                      {['User', 'Role', 'Status', 'Pts Balance', 'Listings', 'Last Active', 'Actions'].map(h => (
                        <th key={h} className={thClass}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {(enhancedUsers.length > 0 ? enhancedUsers : users)
                        .filter(u => !userSearch || (u.full_name + u.email).toLowerCase().includes(userSearch.toLowerCase()))
                        .map((u, i) => (
                          <tr key={u.id} className={`border-b border-gray-50 hover:bg-sky-50/30 cursor-pointer transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}
                            onClick={() => setSelectedUserId(u.id)}>
                            <td className={`${tdClass}`}>
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                                  style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                                  {(u.full_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-800 text-xs leading-tight">{u.full_name || '—'}</p>
                                  <p className="text-gray-400 text-[10px] truncate max-w-[160px]">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className={tdClass}>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === 'landlord' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-[#094f6f]'}`}>{u.role}</span>
                            </td>
                            <td className={tdClass}>
                              <div className="flex flex-col gap-0.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium w-fit ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                                {u.verification_status === 'verified' && <span className="text-[10px] text-teal-600 font-semibold">✓ Verified</span>}
                                {!u.onboarding_completed && <span className="text-[10px] text-amber-500">⚡ Onboarding</span>}
                              </div>
                            </td>
                            <td className={`${tdClass} font-bold text-[#094f6f]`}>{(u.points_balance !== undefined ? u.points_balance : '—').toLocaleString()}</td>
                            <td className={`${tdClass} text-gray-500`}>{u.listing_count !== undefined ? u.listing_count : '—'}</td>
                            <td className={`${tdClass} text-gray-400 text-xs`}>{u.last_active ? new Date(u.last_active).toLocaleDateString('en-AU', {day:'numeric',month:'short'}) : '—'}</td>
                            <td className={tdClass} onClick={e => e.stopPropagation()}>
                              <div className="flex gap-1 flex-wrap">
                                <button onClick={() => setSelectedUserId(u.id)}
                                  className="text-xs px-2 py-1 rounded-lg border border-[#094f6f]/30 text-[#094f6f] hover:bg-[#094f6f]/5 font-medium flex items-center gap-1">
                                  <ExternalLink className="w-3 h-3" /> View
                                </button>
                                <button onClick={() => toggleUser(u.id, u.is_active)}
                                  className={`text-xs px-2 py-1 rounded-lg border transition-colors ${u.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                                  {u.is_active ? 'Off' : 'On'}
                                </button>
                                <button onClick={() => forceLogout(u.id, u.full_name || u.email)}
                                  disabled={forcedOut.has(u.id)}
                                  className={`text-xs px-2 py-1 rounded-lg border transition-colors flex items-center ${forcedOut.has(u.id) ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-orange-200 text-orange-600 hover:bg-orange-50'}`}>
                                  <UserX className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Listings ── */}
            {tab === 'listings' && (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Property Listings</h1>
                <div className="bg-white rounded-2xl border border-sky-100 shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 border-b border-gray-100">
                      {['Name', 'City', 'Price/wk', 'Type', 'Status', 'Actions'].map(h => <th key={h} className={thClass}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {listings.map((l, i) => (
                        <tr key={l.id} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                          <td className={`${tdClass} font-medium text-gray-800 max-w-[200px] truncate`}>{l.name}</td>
                          <td className={`${tdClass} text-gray-500`}>{l.city}</td>
                          <td className={`${tdClass} text-[#094f6f] font-semibold`}>A${l.price_per_week}</td>
                          <td className={`${tdClass} text-gray-500 text-xs`}>{l.room_type}</td>
                          <td className={tdClass}><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{l.status}</span></td>
                          <td className={tdClass}>
                            <div className="flex gap-1">
                              {l.status === 'active' ? <button onClick={() => updateListingStatus(l.id, 'suspended')} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded-lg hover:bg-red-50">Suspend</button>
                                : <button onClick={() => updateListingStatus(l.id, 'active')} className="text-xs px-2 py-1 border border-green-200 text-green-600 rounded-lg hover:bg-green-50">Restore</button>}
                              <button onClick={() => deleteListing(l.id)} className="text-xs px-2 py-1 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Messages ── */}
            {tab === 'messages' && (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Messages</h1>
                <p className="text-sm text-gray-500 mb-6">Monitor conversations. Click a row to expand and view or delete individual messages.</p>
                {conversations.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-sky-100">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                    <p>No conversations yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <div key={conv.conversation_id} className="bg-white rounded-2xl border border-sky-100 shadow-sm overflow-hidden">
                        {/* Conversation header */}
                        <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => loadConvMessages(conv.conversation_id)}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {expandedConv === conv.conversation_id
                              ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800">
                                {conv.participants.map(p => p.name || p.email).join(' ↔ ') || 'Unknown participants'}
                              </p>
                              <p className="text-xs text-gray-400 truncate max-w-sm">{conv.last_message || 'No messages'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                            <span className="text-xs text-gray-400">{conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}</span>
                            <span className="text-xs text-gray-400">{conv.last_message_at?.split('T')[0] || ''}</span>
                            <button onClick={(e) => { e.stopPropagation(); deleteConversation(conv.conversation_id); }}
                              className="text-xs px-2 py-1 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 flex items-center gap-1 transition-colors">
                              <Trash2 className="w-3 h-3" /> Delete all
                            </button>
                          </div>
                        </div>

                        {/* Expanded messages */}
                        {expandedConv === conv.conversation_id && (
                          <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3 space-y-2 max-h-64 overflow-y-auto">
                            {!convMessages[conv.conversation_id] ? (
                              <p className="text-xs text-gray-400 text-center py-2">Loading...</p>
                            ) : convMessages[conv.conversation_id].length === 0 ? (
                              <p className="text-xs text-gray-400 text-center py-2">No messages</p>
                            ) : convMessages[conv.conversation_id].map((msg) => (
                              <div key={msg.message_id} className="flex items-start justify-between gap-3 bg-white rounded-xl p-3 border border-gray-100">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-xs font-semibold text-[#094f6f]">{msg.sender_name}</span>
                                    <span className="text-[10px] text-gray-400">{msg.sender_email}</span>
                                    <span className="text-[10px] text-gray-300">{msg.created_at?.split('T')[0]}</span>
                                    {msg.admin_edited && <span className="text-[9px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">edited by admin</span>}
                                  </div>
                                  {editingMsgId === msg.message_id ? (
                                    <div className="flex gap-2 mt-1">
                                      <input
                                        value={editingMsgText}
                                        onChange={e => setEditingMsgText(e.target.value)}
                                        className="flex-1 border border-[#094f6f]/30 rounded-lg px-2 py-1 text-xs focus:outline-none"
                                        autoFocus
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') saveEditMessage(msg.message_id, conv.conversation_id);
                                          if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgText(''); }
                                        }}
                                      />
                                      <button onClick={() => saveEditMessage(msg.message_id, conv.conversation_id)}
                                        className="text-xs px-2 py-1 text-white rounded-lg hover:opacity-90"
                                        style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                                        Save
                                      </button>
                                      <button onClick={() => { setEditingMsgId(null); setEditingMsgText(''); }}
                                        className="text-xs px-2 py-1 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-700 break-words">{msg.content}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 flex-shrink-0 mt-0.5">
                                  <button onClick={() => { setEditingMsgId(msg.message_id); setEditingMsgText(msg.content); }}
                                    className="text-gray-300 hover:text-[#094f6f] transition-colors" title="Edit message">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  <button onClick={() => deleteMessage(msg.message_id, conv.conversation_id)}
                                    className="text-gray-300 hover:text-red-500 transition-colors" title="Delete message">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── VibeMate Profiles ── */}
            {tab === 'vibe' && (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">VibeMate Profiles</h1>
                <p className="text-sm text-gray-500 mb-6">All student lifestyle profiles submitted for roommate matching.</p>
                {vibeProfiles.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-sky-100">
                    <Heart className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                    <p>No VibeMate profiles yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vibeProfiles.map((p) => (
                      <div key={p.user_id} className="bg-white rounded-2xl border border-sky-100 shadow-sm p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                              {(p.full_name || p.email || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">{p.full_name || '—'}</p>
                              <p className="text-xs text-gray-400">{p.email}</p>
                            </div>
                          </div>
                          <button onClick={() => deleteVibeProfile(p.user_id, p.full_name || p.email)}
                            className="text-gray-300 hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-1.5">
                          {[
                            ['Schedule', p.schedule],
                            ['Smoking', p.smoking],
                            ['Drinking', p.drinking],
                            ['Diet', p.diet],
                            ['Personality', p.personality],
                            ['Budget', p.budget_min && p.budget_max ? `A$${p.budget_min}–${p.budget_max}/wk` : null],
                            ['University', p.university],
                          ].filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs">
                              <span className="text-gray-400">{k}</span>
                              <span className="text-gray-700 font-medium text-right max-w-[150px] truncate">{v}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-300 mt-3 text-right">Updated: {p.updated_at?.split('T')[0] || '—'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* ── Admin Rewards Panel ── */}
        {tab === 'rewards' && <AdminRewards adminKey={ADMIN_KEY} />}
        {tab === 'receipts' && <AdminReceiptsPanel adminKey={ADMIN_KEY} pendingReceipts={pendingReceipts} onRefresh={fetchAll} toast={toast} />}
        {tab === 'verifications' && <AdminVerifications adminKey={ADMIN_KEY} />}
      </div>
    </div>

    {/* User Detail Modal */}
    {selectedUserId && (
      <UserDetailModal
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onActionComplete={() => { fetchAll(); setSelectedUserId(null); }}
      />
    )}
    </>
  );
};

// ── Admin ID Verifications ────────────────────────────────────────────────────
function AdminVerifications({ adminKey }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [notes, setNotes]       = useState({});
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/verification/admin/pending?key=${adminKey}`)
      .then(r => r.json()).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    const r = await fetch(`${API}/api/verification/admin/${id}/approve?key=${adminKey}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ admin_note: notes[id] || '' }),
    });
    const d = await r.json();
    setActionMsg(r.ok ? `✅ Approved — ${d.points_awarded} pts awarded` : `Error: ${d.detail}`);
    load();
  };

  const reject = async (id) => {
    if (!notes[id]) { setActionMsg('Please enter a rejection reason.'); return; }
    await fetch(`${API}/api/verification/admin/${id}/reject?key=${adminKey}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ admin_note: notes[id] }),
    });
    setActionMsg('Verification rejected.');
    load();
  };

  const DOC_LABELS = { drivers_licence: 'Driver\'s Licence', passport: 'Passport', medicare: 'Medicare', national_id: 'National ID', student_id: 'Student ID' };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <Shield className="w-6 h-6 text-[#094f6f]" /> ID Verifications
        <span className="text-sm font-medium text-gray-400">({items.length} pending)</span>
      </h2>
      {actionMsg && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">{actionMsg}</div>}
      {loading ? <div className="text-center py-10 text-gray-400">Loading…</div> : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No pending verifications</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(v => (
            <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex flex-wrap gap-4 items-start mb-4">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{v.user_name}</p>
                  <p className="text-sm text-gray-500">{v.user_email} · <span className="capitalize">{v.user_role}</span> · {DOC_LABELS[v.document_type] || v.document_type}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{v.created_at}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {v.front_url && <a href={v.front_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#094f6f] underline">Front ↗</a>}
                  {v.back_url  && <a href={v.back_url}  target="_blank" rel="noopener noreferrer" className="text-xs text-[#094f6f] underline">Back ↗</a>}
                  {v.selfie_url && <a href={v.selfie_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#094f6f] underline">Selfie ↗</a>}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={notes[v.id] || ''} onChange={e => setNotes(n => ({ ...n, [v.id]: e.target.value }))}
                  placeholder="Rejection reason (required to reject)"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                <button onClick={() => approve(v.id)}
                  className="px-4 py-2 rounded-xl text-white text-sm font-bold hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg,#22c55e,#15803d)' }}>
                  <CheckCircle className="w-4 h-4 inline mr-1" />Approve
                </button>
                <button onClick={() => reject(v.id)}
                  className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50">
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Admin Receipts Panel ──────────────────────────────────────────────────────
function AdminReceiptsPanel({ adminKey, pendingReceipts, onRefresh, toast }) {
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const API = process.env.REACT_APP_BACKEND_URL;
  const fmt = n => Number(n || 0).toLocaleString();

  const approve = async (id) => {
    setApproving(id);
    try {
      const r = await fetch(`${API}/api/receipts/admin/${id}/approve?key=${adminKey}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_note: note }),
      });
      const d = await r.json();
      if (!r.ok) { toast(`Error: ${d.detail}`); }
      else {
        toast(`✅ ${d.message}`);
        setNote('');
        onRefresh();
      }
    } catch { toast('Network error'); }
    setApproving(null);
  };

  const reject = async (id) => {
    setRejecting(id);
    try {
      await fetch(`${API}/api/receipts/admin/${id}/reject?key=${adminKey}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_note: note }),
      });
      toast('Receipt rejected');
      setNote('');
      onRefresh();
    } catch { toast('Network error'); }
    setRejecting(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <Receipt className="w-6 h-6 text-[#094f6f]" /> Rent Receipts
        <span className="ml-2 text-sm font-medium text-gray-400">({pendingReceipts.length} pending)</span>
      </h2>

      {pendingReceipts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No pending receipts</p>
          <p className="text-xs text-gray-400 mt-1">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingReceipts.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex flex-wrap items-start gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{r.property_address}</p>
                  <p className="text-sm text-gray-500">{r.landlord_name} · {r.payment_frequency}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{r.user_name} ({r.user_email}) · {r.receipt_date}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-black text-[#094f6f]">A${r.rent_amount?.toFixed(2)}</p>
                  <p className="text-xs text-emerald-600 font-semibold">{fmt(r.points_preview)} pts on approval</p>
                </div>
              </div>

              {r.receipt_url && (
                <a href={r.receipt_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#094f6f] underline mb-3 block">View receipt image →</a>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <input value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Admin note (optional)"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                <button onClick={() => approve(r.id)} disabled={!!approving || !!rejecting}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-60 transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg,#22c55e,#15803d)' }}>
                  {approving === r.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  Approve (+{fmt(r.points_preview)} pts)
                </button>
                <button onClick={() => reject(r.id)} disabled={!!approving || !!rejecting}
                  className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-bold disabled:opacity-60 hover:bg-red-50 transition-colors">
                  {rejecting === r.id ? <Loader className="w-3.5 h-3.5 animate-spin inline" /> : 'Reject'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Admin Rewards Panel Component ─────────────────────────────────────────────
function AdminRewards({ adminKey }) {
  const [stats, setStats] = useState(null);
  const [topEarners, setTopEarners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustEmail, setAdjustEmail] = useState('');
  const [adjustPts, setAdjustPts] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustMsg, setAdjustMsg] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const fmt = n => Number(n || 0).toLocaleString();

  useEffect(() => {
    fetch(`${API}/api/rewards/admin/stats?key=${adminKey}`)
      .then(r => r.json())
      .then(d => { setStats(d.stats); setTopEarners(d.top_earners || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [adminKey]);

  const handleAdjust = async (e) => {
    e.preventDefault();
    setAdjustLoading(true); setAdjustMsg('');
    try {
      const r = await fetch(`${API}/api/rewards/admin/adjust?key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adjustEmail, points: parseInt(adjustPts), reason: adjustReason }),
      });
      const d = await r.json();
      if (!r.ok) { setAdjustMsg(`Error: ${d.detail}`); }
      else { setAdjustMsg(`Done — new balance: ${fmt(d.new_balance)} pts`); setAdjustEmail(''); setAdjustPts(''); setAdjustReason(''); }
    } catch { setAdjustMsg('Network error'); }
    setAdjustLoading(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader className="w-8 h-8 animate-spin text-[#094f6f]" /></div>;

  const STAT_CARDS = stats ? [
    { label: 'Total in Circulation', value: `${fmt(stats.total_in_circulation)} pts`, sub: `A$${stats.outstanding_liability} liability` },
    { label: 'Lifetime Earned',      value: `${fmt(stats.total_lifetime_earned)} pts`, sub: 'All time' },
    { label: 'Total Redeemed',       value: `${fmt(stats.total_redeemed)} pts`,        sub: 'All time' },
    { label: 'Total Expired',        value: `${fmt(stats.total_expired)} pts`,         sub: 'All time' },
  ] : [];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-6 h-6 text-[#094f6f]" /> Rewards Economy</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium mb-1">{c.label}</p>
            <p className="text-xl font-black text-gray-900">{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Top Earners */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4">Top 10 Earners</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left pb-3 text-xs text-gray-500 font-semibold uppercase">#</th>
                <th className="text-left pb-3 text-xs text-gray-500 font-semibold uppercase">User</th>
                <th className="text-right pb-3 text-xs text-gray-500 font-semibold uppercase">Balance</th>
                <th className="text-right pb-3 text-xs text-gray-500 font-semibold uppercase">Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {topEarners.map((u, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 text-gray-400 font-bold">{i + 1}</td>
                  <td className="py-3">
                    <p className="font-medium text-gray-800">{u.full_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </td>
                  <td className="py-3 text-right font-bold text-[#094f6f]">{fmt(u.balance)}</td>
                  <td className="py-3 text-right text-gray-500">{fmt(u.lifetime_earned)}</td>
                </tr>
              ))}
              {topEarners.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-gray-400 text-sm">No earners yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Adjust */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4">Manual Points Adjustment</h3>
        <form onSubmit={handleAdjust} className="space-y-3 max-w-md">
          <input value={adjustEmail} onChange={e => setAdjustEmail(e.target.value)} type="email" required
            placeholder="User email" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
          <input value={adjustPts} onChange={e => setAdjustPts(e.target.value)} type="number" required
            placeholder="Points (+100 to award, -100 to deduct)" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
          <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} required
            placeholder="Reason for adjustment" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
          {adjustMsg && <p className={`text-sm font-medium ${adjustMsg.startsWith('Error') ? 'text-red-500' : 'text-green-600'}`}>{adjustMsg}</p>}
          <button type="submit" disabled={adjustLoading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            {adjustLoading ? <Loader className="w-4 h-4 animate-spin" /> : null}
            {adjustLoading ? 'Processing…' : 'Apply Adjustment'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminPanel;
